import * as corgi from 'external/dev_april_corgi+/js/corgi';
import {
  Controller,
  Response,
} from 'external/dev_april_corgi+/js/corgi/controller';
import {EmptyDeps} from 'external/dev_april_corgi+/js/corgi/deps';
import {CorgiEvent} from 'external/dev_april_corgi+/js/corgi/events';
import {ACTION, CHANGED, CLOSE} from 'external/dev_april_corgi+/js/emu/events';

import {LabwarePlateSettings, LabwareSettings} from './types/labware_settings';
import {Button, Input} from './widgets';

interface Args {
  settings: LabwareSettings;
  onSave: (settings: LabwareSettings) => void;
}

interface State {
  settings: LabwareSettings;
}

type PlateKey = 'pcrPlate' | 'deepWellPlate' | 'lid';

export class LabwareSettingsEditorController extends Controller<
  Args,
  EmptyDeps,
  HTMLElement,
  State
> {
  private readonly onSave: (settings: LabwareSettings) => void;

  constructor(response: Response<LabwareSettingsEditorController>) {
    super(response);
    this.onSave = response.args.onSave;
  }

  private updatePlate(
    plate: PlateKey,
    update: Partial<LabwarePlateSettings>,
  ): void {
    this.updateState({
      ...this.state,
      settings: {
        ...this.state.settings,
        [plate]: {
          ...this.state.settings[plate],
          ...update,
        },
      },
    });
  }

  pcrPlateModelChanged(e: CorgiEvent<typeof CHANGED>): void {
    this.updatePlate('pcrPlate', {model: e.detail.value});
  }

  pcrPlateHeightChanged(e: CorgiEvent<typeof CHANGED>): void {
    this.updatePlate('pcrPlate', {heightMm: parseFloat(e.detail.value) || 0});
  }

  pcrPlateRowsChanged(e: CorgiEvent<typeof CHANGED>): void {
    const rows = parseInt(e.detail.value, 10) || 0;
    this.updatePlate('pcrPlate', {
      layout: {...this.state.settings.pcrPlate.layout, rows},
    });
  }

  pcrPlateColumnsChanged(e: CorgiEvent<typeof CHANGED>): void {
    const columns = parseInt(e.detail.value, 10) || 0;
    this.updatePlate('pcrPlate', {
      layout: {...this.state.settings.pcrPlate.layout, columns},
    });
  }

  pcrPlateZOffsetChanged(e: CorgiEvent<typeof CHANGED>): void {
    this.updatePlate('pcrPlate', {
      zOffsetMm: parseFloat(e.detail.value) || 0,
    });
  }

  deepWellPlateModelChanged(e: CorgiEvent<typeof CHANGED>): void {
    this.updatePlate('deepWellPlate', {model: e.detail.value});
  }

  deepWellPlateHeightChanged(e: CorgiEvent<typeof CHANGED>): void {
    this.updatePlate('deepWellPlate', {
      heightMm: parseFloat(e.detail.value) || 0,
    });
  }

  deepWellPlateRowsChanged(e: CorgiEvent<typeof CHANGED>): void {
    const rows = parseInt(e.detail.value, 10) || 0;
    this.updatePlate('deepWellPlate', {
      layout: {...this.state.settings.deepWellPlate.layout, rows},
    });
  }

  deepWellPlateColumnsChanged(e: CorgiEvent<typeof CHANGED>): void {
    const columns = parseInt(e.detail.value, 10) || 0;
    this.updatePlate('deepWellPlate', {
      layout: {...this.state.settings.deepWellPlate.layout, columns},
    });
  }

  deepWellPlateZOffsetChanged(e: CorgiEvent<typeof CHANGED>): void {
    this.updatePlate('deepWellPlate', {
      zOffsetMm: parseFloat(e.detail.value) || 0,
    });
  }

  lidModelChanged(e: CorgiEvent<typeof CHANGED>): void {
    this.updatePlate('lid', {model: e.detail.value});
  }

  lidHeightChanged(e: CorgiEvent<typeof CHANGED>): void {
    this.updatePlate('lid', {heightMm: parseFloat(e.detail.value) || 0});
  }

  lidZOffsetChanged(e: CorgiEvent<typeof CHANGED>): void {
    this.updatePlate('lid', {zOffsetMm: parseFloat(e.detail.value) || 0});
  }

  save(): void {
    this.onSave(this.state.settings);
    this.trigger(CLOSE, {kind: 'resolve'});
  }
}

export function makeLabwareSettingsEditor(args: Args) {
  return <LabwareSettingsEditor {...args} />;
}

function LabwareSettingsEditor(
  args: Args,
  state: State | undefined,
  updateState: (newState: State) => void,
) {
  if (!state) {
    state = {
      settings: {...args.settings},
    };
  }

  return (
    <div
      className="bg-neutral-800 flex flex-col gap-4 min-w-[600px] p-4 rounded"
      js={corgi.bind({
        controller: LabwareSettingsEditorController,
        args,
        state: [state, updateState],
      })}
    >
      <div className="font-semibold text-lg">Labware Settings</div>

      <div className="flex flex-col gap-6">
        <PlateRow
          label="PCR Plate"
          plate={state.settings.pcrPlate}
          modelChangeAction="pcrPlateModelChanged"
          heightChangeAction="pcrPlateHeightChanged"
          rowsChangeAction="pcrPlateRowsChanged"
          columnsChangeAction="pcrPlateColumnsChanged"
          zOffsetChangeAction="pcrPlateZOffsetChanged"
          placeholder="biorad_96_wellplate_200ul_pcr"
        />

        <PlateRow
          label="Deep Well Plate"
          plate={state.settings.deepWellPlate}
          modelChangeAction="deepWellPlateModelChanged"
          heightChangeAction="deepWellPlateHeightChanged"
          rowsChangeAction="deepWellPlateRowsChanged"
          columnsChangeAction="deepWellPlateColumnsChanged"
          zOffsetChangeAction="deepWellPlateZOffsetChanged"
          placeholder="nest_96_wellplate_2ml_deep"
        />

        <LidRow
          label="Lid (optional)"
          plate={state.settings.lid}
          modelChangeAction="lidModelChanged"
          heightChangeAction="lidHeightChanged"
          zOffsetChangeAction="lidZOffsetChanged"
          placeholder="Leave empty to disable lids"
        />
      </div>

      <div className="text-neutral-400 text-sm">
        Enter Opentrons labware names. Height is the total labware height in mm.
        Z offset adjusts pipette height (positive = up).
      </div>

      <div className="flex justify-end">
        <Button unboundEvents={{corgi: [[ACTION, 'save']]}}>Save</Button>
      </div>
    </div>
  );
}

interface PlateRowProps {
  label: string;
  plate: LabwarePlateSettings;
  modelChangeAction: string;
  heightChangeAction: string;
  rowsChangeAction: string;
  columnsChangeAction: string;
  zOffsetChangeAction: string;
  placeholder?: string;
}

function PlateRow({
  label,
  plate,
  modelChangeAction,
  heightChangeAction,
  rowsChangeAction,
  columnsChangeAction,
  zOffsetChangeAction,
  placeholder,
}: PlateRowProps) {
  return (
    <div className="flex flex-col gap-2">
      <div className="text-sm font-medium">{label}</div>
      <div className="flex flex-col gap-2">
        <div className="flex gap-2 items-center">
          <Input
            className="bg-neutral-700 text-sm flex-1"
            type="text"
            value={plate.model}
            placeholder={placeholder}
            unboundEvents={{corgi: [[CHANGED, modelChangeAction]]}}
          />
        </div>
        <div className="flex gap-4 items-center text-sm">
          <div className="flex items-center gap-1">
            <span className="text-neutral-400">Height:</span>
            <Input
              className="bg-neutral-700 text-sm w-16"
              type="number"
              step="0.01"
              value={plate.heightMm.toString()}
              unboundEvents={{corgi: [[CHANGED, heightChangeAction]]}}
            />
            <span className="text-neutral-400">mm</span>
          </div>
          <div className="flex items-center gap-1">
            <span className="text-neutral-400">Layout:</span>
            <Input
              className="bg-neutral-700 text-sm w-12"
              type="number"
              value={plate.layout.rows.toString()}
              unboundEvents={{corgi: [[CHANGED, rowsChangeAction]]}}
            />
            <span className="text-neutral-400">x</span>
            <Input
              className="bg-neutral-700 text-sm w-12"
              type="number"
              value={plate.layout.columns.toString()}
              unboundEvents={{corgi: [[CHANGED, columnsChangeAction]]}}
            />
          </div>
          <div className="flex items-center gap-1">
            <span className="text-neutral-400">Z offset:</span>
            <Input
              className="bg-neutral-700 text-sm w-16"
              type="number"
              step="0.1"
              value={plate.zOffsetMm.toString()}
              unboundEvents={{corgi: [[CHANGED, zOffsetChangeAction]]}}
            />
            <span className="text-neutral-400">mm</span>
          </div>
        </div>
      </div>
    </div>
  );
}

interface LidRowProps {
  label: string;
  plate: LabwarePlateSettings;
  modelChangeAction: string;
  heightChangeAction: string;
  zOffsetChangeAction: string;
  placeholder?: string;
}

function LidRow({
  label,
  plate,
  modelChangeAction,
  heightChangeAction,
  zOffsetChangeAction,
  placeholder,
}: LidRowProps) {
  return (
    <div className="flex flex-col gap-2">
      <div className="text-sm font-medium">{label}</div>
      <div className="flex flex-col gap-2">
        <div className="flex gap-2 items-center">
          <Input
            className="bg-neutral-700 text-sm flex-1"
            type="text"
            value={plate.model}
            placeholder={placeholder}
            unboundEvents={{corgi: [[CHANGED, modelChangeAction]]}}
          />
        </div>
        {plate.model && (
          <div className="flex gap-4 items-center text-sm">
            <div className="flex items-center gap-1">
              <span className="text-neutral-400">Height:</span>
              <Input
                className="bg-neutral-700 text-sm w-16"
                type="number"
                step="0.01"
                value={plate.heightMm.toString()}
                unboundEvents={{corgi: [[CHANGED, heightChangeAction]]}}
              />
              <span className="text-neutral-400">mm</span>
            </div>
            <div className="flex items-center gap-1">
              <span className="text-neutral-400">Z offset:</span>
              <Input
                className="bg-neutral-700 text-sm w-16"
                type="number"
                step="0.1"
                value={plate.zOffsetMm.toString()}
                unboundEvents={{corgi: [[CHANGED, zOffsetChangeAction]]}}
              />
              <span className="text-neutral-400">mm</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
